import { useEffect, useRef, useState } from 'react';
import { ChevronDown, X } from 'lucide-react';
import type { FilterwerteDto } from '../../../lib/apiStatsApi';
import type { FilterZustand, Nur, Zeitwahl } from './filterZustand';
import { zeitraumVon } from './filterZustand';

const ZEITWAHLEN: Array<{ wert: Zeitwahl; label: string }> = [
  { wert: 'heute', label: 'Heute' },
  { wert: '7t', label: '7 Tage' },
  { wert: '30t', label: '30 Tage' },
  { wert: '90t', label: '90 Tage' },
  { wert: 'monat', label: 'Dieser Monat' },
  { wert: 'vormonat', label: 'Letzter Monat' },
];

const NUR_WAHLEN: Array<{ wert: Nur; label: string }> = [
  { wert: 'alles', label: 'Alles' },
  { wert: 'fehler', label: 'Nur Fehler' },
  { wert: 'erfolge', label: 'Nur Erfolge' },
];

interface FilterLeisteProps {
  filter: FilterZustand;
  werte: FilterwerteDto | null;
  darkMode: boolean;
  onChange: (naechster: FilterZustand) => void;
}

/** Mehrfachauswahl als schlichtes Kontrollkästchen-Menü — kein neues Paket. */
function MehrfachAuswahl({ label, gewaehlt, eintraege, darkMode, onToggle, onLeeren }: {
  label: string;
  gewaehlt: number;
  eintraege: Array<{ wert: string; text: string; aktiv: boolean }>;
  darkMode: boolean;
  onToggle: (wert: string) => void;
  onLeeren: () => void;
}) {
  const [offen, setOffen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!offen) return;
    const zu = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOffen(false);
    };
    document.addEventListener('mousedown', zu);
    return () => document.removeEventListener('mousedown', zu);
  }, [offen]);

  const rand = darkMode ? 'border-slate-700' : 'border-slate-300';
  const flaeche = darkMode ? 'bg-slate-800 text-slate-200' : 'bg-white text-slate-700';

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOffen(o => !o)}
        className={`flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg border ${rand} ${flaeche} ${gewaehlt > 0 ? 'font-medium' : ''}`}
      >
        {label}
        {gewaehlt > 0 && (
          <span className="px-1.5 rounded-full text-xs bg-blue-600 text-white">{gewaehlt}</span>
        )}
        <ChevronDown className="w-3.5 h-3.5 opacity-60" />
      </button>
      {offen && (
        <div className={`absolute z-20 mt-1 min-w-[240px] max-h-72 overflow-y-auto rounded-lg border shadow-lg ${rand} ${flaeche}`}>
          {eintraege.length === 0 && (
            <p className={`px-3 py-2 text-sm ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Keine Einträge</p>
          )}
          {eintraege.map(e => (
            <label key={e.wert} className={`flex items-center gap-2 px-3 py-1.5 text-sm cursor-pointer ${darkMode ? 'hover:bg-slate-700' : 'hover:bg-slate-50'}`}>
              <input type="checkbox" checked={e.aktiv} onChange={() => onToggle(e.wert)} />
              <span className="text-left">{e.text}</span>
            </label>
          ))}
          {gewaehlt > 0 && (
            <button
              type="button"
              onClick={() => { onLeeren(); setOffen(false); }}
              className={`w-full text-left px-3 py-1.5 text-sm border-t ${rand} ${darkMode ? 'text-slate-400 hover:bg-slate-700' : 'text-slate-500 hover:bg-slate-50'}`}
            >
              Auswahl leeren
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export default function FilterLeiste({ filter, werte, darkMode, onChange }: FilterLeisteProps) {
  const rand = darkMode ? 'border-slate-700' : 'border-slate-300';
  const flaeche = darkMode ? 'bg-slate-800 text-slate-200' : 'bg-white text-slate-700';
  const grenzen = zeitraumVon(filter.zeitwahl, filter.von, filter.bis);

  // Schlüssel-Auswahl auf die gewählten Kunden eingrenzen.
  const schluesselEintraege = (werte?.schluessel ?? [])
    .filter(s => filter.mandanten.length === 0 || filter.mandanten.includes(s.mandantWert))
    .map(s => ({
      wert: String(s.id),
      text: `${s.name}${s.widerrufen ? ' (widerrufen)' : ''} — ${s.prefix}…`,
      aktiv: filter.schluessel.includes(s.id),
    }));

  const mandantenEintraege = [
    ...(werte?.mandanten ?? []).map(m => ({ wert: m.wert, text: m.name, aktiv: filter.mandanten.includes(m.wert) })),
    { wert: 'ohne', text: 'Ohne gültigen Schlüssel / ohne Route', aktiv: filter.mandanten.includes('ohne') },
  ];

  return (
    <div className="flex flex-wrap items-center gap-2">
      <div className={`inline-flex rounded-lg border overflow-hidden ${rand}`}>
        {ZEITWAHLEN.map(z => (
          <button
            key={z.wert}
            type="button"
            onClick={() => onChange({ ...filter, zeitwahl: z.wert })}
            className={`px-3 py-1.5 text-sm font-medium transition-colors ${
              filter.zeitwahl === z.wert
                ? 'bg-blue-600 text-white'
                : darkMode ? 'text-slate-300 hover:bg-slate-700' : 'text-slate-600 hover:bg-slate-100'
            }`}
          >
            {z.label}
          </button>
        ))}
      </div>

      {/* Freie Wahl von–bis: das Ändern eines Datums schaltet auf 'frei' um. */}
      <div className="flex items-center gap-1">
        <input
          type="date"
          value={filter.zeitwahl === 'frei' ? (filter.von || grenzen.von) : grenzen.von}
          onChange={e => onChange({ ...filter, zeitwahl: 'frei', von: e.target.value, bis: filter.zeitwahl === 'frei' ? (filter.bis || grenzen.bis) : grenzen.bis })}
          className={`px-2 py-1.5 text-sm rounded-lg border ${rand} ${flaeche}`}
          aria-label="Zeitraum von"
        />
        <span className={darkMode ? 'text-slate-500' : 'text-slate-400'}>–</span>
        <input
          type="date"
          value={filter.zeitwahl === 'frei' ? (filter.bis || grenzen.bis) : grenzen.bis}
          onChange={e => onChange({ ...filter, zeitwahl: 'frei', bis: e.target.value, von: filter.zeitwahl === 'frei' ? (filter.von || grenzen.von) : grenzen.von })}
          className={`px-2 py-1.5 text-sm rounded-lg border ${rand} ${flaeche}`}
          aria-label="Zeitraum bis"
        />
      </div>

      <MehrfachAuswahl
        label="Kunde"
        gewaehlt={filter.mandanten.length}
        eintraege={mandantenEintraege}
        darkMode={darkMode}
        onToggle={wert => {
          const mandanten = filter.mandanten.includes(wert)
            ? filter.mandanten.filter(m => m !== wert)
            : [...filter.mandanten, wert];
          // Schlüssel fremder Kunden aus der Auswahl nehmen, sonst filtert man Unsichtbares.
          const erlaubt = new Set((werte?.schluessel ?? []).filter(s => mandanten.length === 0 || mandanten.includes(s.mandantWert)).map(s => s.id));
          onChange({ ...filter, mandanten, schluessel: filter.schluessel.filter(id => erlaubt.has(id)) });
        }}
        onLeeren={() => onChange({ ...filter, mandanten: [] })}
      />

      <MehrfachAuswahl
        label="Schlüssel"
        gewaehlt={filter.schluessel.length}
        eintraege={schluesselEintraege}
        darkMode={darkMode}
        onToggle={wert => {
          const id = Number(wert);
          const schluessel = filter.schluessel.includes(id)
            ? filter.schluessel.filter(s => s !== id)
            : [...filter.schluessel, id];
          onChange({ ...filter, schluessel });
        }}
        onLeeren={() => onChange({ ...filter, schluessel: [] })}
      />

      <div className={`inline-flex rounded-lg border overflow-hidden ${rand}`}>
        {NUR_WAHLEN.map(n => (
          <button
            key={n.wert}
            type="button"
            onClick={() => onChange({ ...filter, nur: n.wert })}
            className={`px-3 py-1.5 text-sm font-medium transition-colors ${
              filter.nur === n.wert
                ? 'bg-blue-600 text-white'
                : darkMode ? 'text-slate-300 hover:bg-slate-700' : 'text-slate-600 hover:bg-slate-100'
            }`}
          >
            {n.label}
          </button>
        ))}
      </div>

      {filter.endpunkt && (
        <span className={`flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg border ${rand} ${flaeche}`}>
          <span className="font-mono">{filter.endpunkt}</span>
          <button type="button" onClick={() => onChange({ ...filter, endpunkt: '' })} aria-label="Endpunkt-Filter entfernen">
            <X className="w-3.5 h-3.5 opacity-60" />
          </button>
        </span>
      )}
    </div>
  );
}
